/**
 * Severity treatment for the notification centre.
 *
 * The four real tiers replace the decorative 4-value `priority` enum:
 *
 * | Tier              | Treatment                                          |
 * |-------------------|----------------------------------------------------|
 * | `critical`        | red, always first, always in the bell count         |
 * | `action_required` | amber, "Action needed" pill, in the bell count      |
 * | `info`            | blue, in the bell count                            |
 * | `background`      | grey, Activity feed only — never in the bell count  |
 *
 * Rows written before the template pipeline carry no `severity`, so
 * {@link resolveSeverity} derives one from the legacy `priority`. That mapping
 * deliberately never returns `background`: a historic row must not become
 * invisible because of a migration it predates.
 */
import { AlertOctagon, AlertTriangle, Info, Moon, type LucideIcon } from 'lucide-react';

import {
  NOTIFICATION_SEVERITY_RANK,
  type Notification,
  type NotificationSeverity,
} from '../../lib/api';

export interface SeverityMeta {
  severity: NotificationSeverity;
  label: string;
  /** Short pill copy for the row badge. */
  badgeLabel: string;
  icon: LucideIcon;
  /** Classes for the round icon chip on the left of a row. */
  chipClass: string;
  /** Classes for the severity pill. */
  badgeClass: string;
  /** Left border accent applied to critical / action_required rows. */
  accentClass: string;
}

export const SEVERITY_META: Record<NotificationSeverity, SeverityMeta> = {
  critical: {
    severity: 'critical',
    label: 'Critical',
    badgeLabel: 'Critical',
    icon: AlertOctagon,
    chipClass: 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400',
    badgeClass:
      'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-300',
    accentClass: 'border-l-4 border-l-red-500',
  },
  action_required: {
    severity: 'action_required',
    label: 'Action required',
    badgeLabel: 'Action needed',
    icon: AlertTriangle,
    chipClass: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400',
    badgeClass:
      'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-900/30 dark:text-amber-300',
    accentClass: 'border-l-4 border-l-amber-500',
  },
  info: {
    severity: 'info',
    label: 'Info',
    badgeLabel: 'Info',
    icon: Info,
    chipClass: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400',
    badgeClass:
      'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-900/30 dark:text-blue-300',
    accentClass: 'border-l-4 border-l-transparent',
  },
  background: {
    severity: 'background',
    label: 'Activity',
    badgeLabel: 'Activity',
    icon: Moon,
    chipClass: 'text-gray-500 bg-gray-100 dark:bg-gray-800 dark:text-gray-400',
    badgeClass:
      'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
    accentClass: 'border-l-4 border-l-transparent',
  },
};

const SEVERITIES: readonly NotificationSeverity[] = [
  'critical',
  'action_required',
  'info',
  'background',
];

/** Narrow an arbitrary string to a known severity tier. */
export function isNotificationSeverity(value: string): value is NotificationSeverity {
  return SEVERITIES.indexOf(value as NotificationSeverity) !== -1;
}

/**
 * The tier a row should be treated as.
 *
 * Uses the row's own `severity` when the payload carries one; otherwise maps
 * the legacy `priority`. Never downgrades a legacy row to `background`.
 */
export function resolveSeverity(
  notification: Pick<Notification, 'severity' | 'priority'>
): NotificationSeverity {
  const raw = notification.severity;
  if (raw && isNotificationSeverity(raw)) return raw;

  switch (notification.priority) {
    case 'urgent':
      return 'critical';
    case 'high':
      return 'action_required';
    default:
      return 'info';
  }
}

/** True when `severity` is at or above `floor` in urgency. */
export function isAtLeast(
  severity: NotificationSeverity,
  floor: NotificationSeverity
): boolean {
  return NOTIFICATION_SEVERITY_RANK[severity] >= NOTIFICATION_SEVERITY_RANK[floor];
}

/** The most urgent tier in a list; `info` for an empty list. */
export function highestSeverity(
  severities: readonly NotificationSeverity[]
): NotificationSeverity {
  let best: NotificationSeverity = 'background';
  let seen = false;
  severities.forEach((severity) => {
    seen = true;
    if (NOTIFICATION_SEVERITY_RANK[severity] > NOTIFICATION_SEVERITY_RANK[best]) {
      best = severity;
    }
  });
  return seen ? best : 'info';
}

/** Severity metadata, falling back to `info` for anything unrecognised. */
export function getSeverityMeta(severity: string): SeverityMeta {
  return isNotificationSeverity(severity)
    ? SEVERITY_META[severity]
    : SEVERITY_META.info;
}

/** `background` rows are excluded from the bell count and the default feed. */
export function isBackground(severity: NotificationSeverity): boolean {
  return severity === 'background';
}
